import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types/firestore';

// ============================================================================
// LOADING SPINNER
// ============================================================================

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-blue-600"></div>
        <p className="mt-4 text-slate-600">Loading...</p>
      </div>
    </div>
  );
}

// ============================================================================
// PROTECTED ROUTE COMPONENT
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  requireAdmin?: boolean;
  requireManagerOrAdmin?: boolean;
  requireHrRole?: 'hr_admin' | 'hr_coordinator' | true;
  /**
   * Admit only a user whose role this predicate accepts. For a gate whose server and rules
   * twins compare roles case-insensitively (e.g. isOfficeContractReader), where the
   * exact-match `requiredRole` would lock out a role stored in another case. Optional:
   * routes that do not pass it are unchanged.
   */
  allowRole?: (role: unknown) => boolean;
  /**
   * Admit only a user this predicate accepts, for a gate that depends on more than the role
   * (e.g. Issue Triage, which also admits two named people by login). Used INSTEAD of
   * `requiredRole`, not alongside it. Optional: routes that do not pass it are unchanged.
   */
  allowUser?: (user: NonNullable<ReturnType<typeof useAuth>['user']>) => boolean;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requireAdmin = false,
  requireManagerOrAdmin = false,
  requireHrRole,
  allowRole,
  allowUser,
}: ProtectedRouteProps) {
  const { user, loading, isAdmin, isManagerOrAdmin } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is active
  if (!user.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Account Inactive
          </h2>
          <p className="text-slate-600 mb-6">
            Your account has been deactivated. Please contact your administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  // UX guard only — prevents investor users from seeing employee UI shell.
  // The actual security boundary is Firestore rules + withModuleAccess on Cloud Functions.
  // An investor bypassing this client-side check would see empty pages (no data returned).
  if (user.role === 'Investor' && !location.pathname.startsWith('/investors')) {
    return <Navigate to="/investors" replace />;
  }

  // Check for admin requirement
  if (requireAdmin && !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Access Denied
          </h2>
          <p className="text-slate-600 mb-6">
            You need administrator privileges to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check for manager or admin requirement
  if (requireManagerOrAdmin && !isManagerOrAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Access Denied
          </h2>
          <p className="text-slate-600 mb-6">
            You need manager or administrator privileges to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check for specific role requirement (an exact-match list and/or a role predicate)
  if (requiredRole || allowRole || allowUser) {
    const roles = requiredRole ? ([] as UserRole[]).concat(requiredRole) : null;
    const hasRequiredRole =
      (!roles || roles.includes(user.role)) &&
      (!allowRole || allowRole(user.role)) &&
      (!allowUser || allowUser(user));

    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Access Denied
            </h2>
            <p className="text-slate-600 mb-6">
              You don't have the required role to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // Check for HR role requirement.
  //
  // Both branches previously redirected silently to "/". HR is visible in the
  // module switcher to anyone carrying an hrRole OR an explicit P9 entry in
  // enabledModules (see ModuleContext), so a user could see the module, click
  // it, and land back on the dashboard with no explanation and nothing to act
  // on — indistinguishable from a broken link. Say what is missing and who can
  // grant it instead.
  if (requireHrRole) {
    const userHrRole = user?.hrRole;
    const insufficient =
      requireHrRole !== true && userHrRole !== requireHrRole && userHrRole !== 'hr_admin';
    if (!userHrRole || insufficient) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              HR access not enabled
            </h2>
            <p className="text-slate-600 mb-6">
              {userHrRole
                ? 'This page is limited to HR administrators. Your account has HR access, but not at the level this page requires.'
                : 'Your account does not have an HR role yet. An administrator can grant one — HR Coordinator for day-to-day records, or HR Administrator for payroll and system links.'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
}
